import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  padding: 20px;
  box-sizing: border-box;
  min-height: calc(100vh - 60px);

  &.final-submission {
    .table-container-wrapper {
      margin-top: 20px;
    }

    .table-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f8f9fa;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 20px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const UploadSection = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const CameraBorder = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border: 2px solid #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;

  &::before,
  &::after,
  span::before,
  span::after {
    content: "";
    position: absolute;
    width: 40px;
    height: 40px;
    background: transparent;
    transition: 1s;
  }

  &::before {
    top: -2px;
    left: -2px;
    border-top: 5px solid #0e93cd;
    border-left: 5px solid #0e93cd;
  }

  &::after {
    top: -2px;
    right: -2px;
    border-top: 5px solid #0e93cd;
    border-right: 5px solid #0e93cd;
  }

  span::before {
    bottom: -2px;
    left: -2px;
    border-bottom: 5px solid #0e93cd;
    border-left: 5px solid #0e93cd;
  }

  span::after {
    bottom: -2px;
    right: -2px;
    border-bottom: 5px solid #0e93cd;
    border-right: 5px solid #0e93cd;
  }
`;

const WebcamWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px dashed #ccc;
  padding: 20px;
  max-width: 300px;
  height: 300px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
  width: 100%;
  max-width: 600px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 120px;

  &.cancel-btn {
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;

    &:hover {
      background-color: #e9ecef;
    }
  }

  &.submit-btn {
    background-color: #0e93cd;
    color: white;

    &:hover {
      background-color: #0b7aa9;
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadPrompt = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const UploadHint = styled.p`
  color: #666;
  font-size: 14px;
  margin: 10px 0;
`;

const PreviewSection = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
  
  img {
    width: 100%;
    max-width: 100%;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Camera = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { questionId } = useParams();
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef();

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length===0) return;
    if (files.length + selectedImages.length > 5) {
      alert('Maximum 5 images allowed');
      window.location.reload();
      return;
    }

    if (files.some(f=>f.size>5*1024*1024)) {
      alert('Each image must be under 5 MB');
      event.target.value='';
      return;
    }

    const newPreviews = [];
    files.forEach(file=>{
      const reader=new FileReader();
      reader.onload=()=>{ setPreviewUrls(prev=>[...prev, reader.result]); };
      reader.readAsDataURL(file);
    });
    setSelectedImages(prev=>[...prev, ...files]);
    event.target.value='';
  };

  const handleSubmit = async () => {
    if (selectedImages.length===0) { alert('Please select at least one image'); return; }

    setIsProcessing(true);

    try {
      if (location.state?.onImageCapture) {
        await location.state.onImageCapture(selectedImages);
      }
      
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: {
            ...location.state,
            currentQuestionIndex: location.state.currentQuestionIndex,
            className: location.state.className
          }
        });
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error uploading image');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container>
      <Title>Upload or Capture Image</Title>
      <UploadSection>
        <CameraBorder>
          <WebcamWrapper>
            {previewUrls.length===0 ? <span>Preview</span> : previewUrls.map((url,i)=><img key={i} src={url} alt={`prev${i}`} />)}
          </WebcamWrapper>
        </CameraBorder>
        <ButtonContainer>
          <Button className="cancel-btn" onClick={() => navigate(-1)}>Cancel</Button>
          <Button className="submit-btn" onClick={handleSubmit} disabled={isProcessing || selectedImages.length===0}>Done</Button>
        </ButtonContainer>
        <UploadPrompt>
          <label htmlFor="fileInput">Upload Image</label>
          <FileInput id="fileInput" type="file" multiple accept="image/*" onChange={handleImageSelect} />
        </UploadPrompt>
      </UploadSection>
    </Container>
  );
};

export default Camera;